import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/atoms/Avatar/Avatar";
import { Button } from "@/components/ui/atoms/Button/Button";
import { Input } from "@/components/ui/atoms/Input/Input";
import ButtonGroup from "@/components/ui/molecules/ButtonGroup/ButtonGroup";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/atoms/Form/form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CiviliteEnum, UserProfile } from "@/types/settings.types";
import { useMutation } from "@apollo/client";
import { SAVE_USER_SETTINGS } from "@/graphql/admin/settings/SettingsMutations";
import { useSelectedLinkContext } from "@/contexts";

type AdminSettingsUserInformationsFormProps = {
  initialUserProfile: UserProfile;
  setEditMode: (value: boolean) => void;
  refreshData: () => void;
}

const CIVILITE_OPTIONS = [{label: 'M.', value: CiviliteEnum.HOMME}, {label: 'MME.', value: CiviliteEnum.FEMME}];

const AdminSettingsUserInformationsForm = ({ initialUserProfile, setEditMode, refreshData }: AdminSettingsUserInformationsFormProps): JSX.Element => {

  const { selectedLink } = useSelectedLinkContext();

  const formSchema = z.object({
    firstName: z.string().min(1, 'Champ obligatoire'),
    lastName: z.string().min(1, 'Champ obligatoire'),
    birthday: z.string().optional(),
    postalAddress: z.object({
      address: z.string().nullish().optional(),
      zipcode: z.string().refine((value) => /^\d{5}$/.test(value), 'Code postal invalide')
        .or(z.string().refine((value) => /^(?:[A-Z]\d[A-Z]\d[A-Z]\d)$/.test(value), 'Code postal invalide')),
      city: z.string().nullish().optional(),
      country: z.string().nullish().optional()
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...initialUserProfile,
      birthday: new Date(parseInt(initialUserProfile.birthday)).toLocaleDateString() !== 'Invalid Date' ? new Date(parseInt(initialUserProfile.birthday)).toLocaleDateString() : ''
    }
  });

 

  const [saveUserProfileGQL] = useMutation(
    SAVE_USER_SETTINGS,
  );

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    let valuesCopy = values;
    const birthday = new Date(parseInt(values.birthday.split('/')[2]), parseInt(values.birthday.split('/')[1] - 1), parseInt(values.birthday.split('/')[0]))
    if (values.birthday) {
      valuesCopy = {
        ...values,
        birthday: birthday.getTime().toString()
      }
    }
    saveUserProfileGQL({
      variables: {
        saveUserProfileInput: {
          channel: selectedLink.channelKey,
          profile: valuesCopy,
        },
      },
    }).then(() => refreshData());
    setEditMode(false);
  }

  return initialUserProfile && (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col items-center gap-2w p-2w bg-neutral-100">
        {/* <div className="flex flex-col items-center gap-4w p-2w w-full rounded-2xl bg-white">
          <Avatar className="w-[160px]">
            <AvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
            <AvatarFallback>Avatar</AvatarFallback>
          </Avatar> 
          <Button type="button" variant="secondary" className="w-fit">
            <FontAwesomeIcon icon={faCamera} />
            Importer une photo
          </Button>
        </div> */}
        <div className="flex flex-col gap-2w p-2w w-full rounded-2xl bg-white">
          <div className="flex flex-col gap-0">
            <h2 className="text-subtitle1">Informations principales</h2>
            <span className="text-body2">*Champs obligatoires</span>
          </div>
          {/* <div className="flex flex-col gap-1v">
            <FormField
              control={form.control} 
              name="civilite"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-1v">
                  <FormLabel>Civilité*</FormLabel>
                  <FormControl>
                    <ButtonGroup options={CIVILITE_OPTIONS} {...field} />
                  </FormControl>
                </FormItem>
            )} />
          </div> */}
          <FormField
            control={form.control} 
            name="firstName"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1v">
                <FormLabel>Prénom*</FormLabel>
                <FormControl>
                  <Input placeholder='Ex: Pierre' {...field} />
                </FormControl>
                <FormMessage></FormMessage>
              </FormItem>
          )} />
          <FormField
            control={form.control} 
            name="lastName"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1v">
                <FormLabel>Nom*</FormLabel>
                <FormControl>
                  <Input placeholder='Ex: Dupont' {...field} />
                </FormControl>
                <FormMessage></FormMessage>
              </FormItem>
          )} />
        </div>
        <div className="flex flex-col gap-2w p-2w w-full rounded-2xl bg-white">
          <div className="flex flex-col gap-0">
            <h2 className="text-subtitle1">Informations complémentaires</h2>
            <span className="text-body2">*Champs obligatoires</span>
          </div>
          <FormField
            control={form.control} 
            name="birthday"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1v">
                <FormLabel>Date de naissance (JJ/MM/AAAA)</FormLabel>
                <FormControl>
                  <Input placeholder='Ex: 01/01/1950' {...field} />
                </FormControl>
                <FormMessage></FormMessage>
              </FormItem>
          )} />
          <FormField
            control={form.control} 
            name="postalAddress.address"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1v">
                <FormLabel>Adresse</FormLabel>
                <FormControl>
                  <Input placeholder='Ex: 1 rue de la gare' {...field} />
                </FormControl>
                <FormMessage></FormMessage>
              </FormItem>
          )} />
          <div className="flex flex-row w-full gap-2w">
            <div className="flex flex-col w-[50%] gap-2w">
              <FormField
                control={form.control} 
                name="postalAddress.zipcode"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1v">
                    <FormLabel>Code Postal*</FormLabel>
                    <FormControl>
                      <Input placeholder='Ex: 44000' {...field} />
                    </FormControl>
                    <FormMessage></FormMessage>
                  </FormItem>
              )} />
              <FormField
                control={form.control} 
                name="postalAddress.country"
                disabled
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1v">
                    <FormLabel>Pays</FormLabel>
                    <FormControl>
                      <Input placeholder='Ex: France' {...field} />
                    </FormControl>
                    <FormMessage></FormMessage>
                  </FormItem>
              )} />
              {/* <FormField
                control={form.control} 
                name="telephoneFixe"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1v">
                    <FormLabel>Téléphone fixe</FormLabel>
                    <FormControl>
                      <Input placeholder='01 02 03 04 05' {...field} />
                    </FormControl>
                  </FormItem>
              )} /> */}
            </div>
            <div className="flex flex-col w-[50%] gap-2w">
              <FormField
                control={form.control} 
                name="postalAddress.city"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1v">
                    <FormLabel>Ville</FormLabel>
                    <FormControl>
                      <Input placeholder='Ex: Nantes' {...field} />
                    </FormControl>
                    <FormMessage></FormMessage>
                  </FormItem>
              )} />
              {/* <FormField
                control={form.control} 
                name="skypeId"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1v">
                    <FormLabel>Identifiant Skype</FormLabel>
                    <FormControl>
                      <Input placeholder='pierre.dupont' {...field} />
                    </FormControl>
                  </FormItem>
              )} /> */}
              {/* <FormField
                control={form.control} 
                name="telephonePortable"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1v">
                    <FormLabel>Téléphone portable</FormLabel>
                    <FormControl>
                      <Input placeholder='06 05 04 03 02' {...field} />
                    </FormControl>
                  </FormItem>
              )} /> */}
            </div>
          </div>
          {/* <FormField
            control={form.control} 
            name="email1"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1v">
                <FormLabel>Email 1</FormLabel>
                <FormControl>
                  <Input placeholder='pierre.dupont@mail.fr' {...field} />
                </FormControl>
              </FormItem>
          )} /> */}
          {/* <FormField
            control={form.control} 
            name="email2"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1v">
                <FormLabel>Email 2</FormLabel>
                <FormControl>
                  <Input placeholder='pierre2.dupont@mail.fr' {...field} />
                </FormControl>
              </FormItem>
          )} /> */}
        </div>
        <div className="flex flex-row justify-center gap-2w p-2w w-full rounded-2xl bg-white">
          <Button variant="secondary" className="w-fit" onClick={() => setEditMode(false)}>Annuler les modifications</Button>
          <Button className="w-fit" type="submit">Enregistrer les modifications</Button>
        </div>
      </form>
    </Form>);
}

export default AdminSettingsUserInformationsForm;